import { Building2, GitBranch, Users } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { GarageKpis, QuickActions, RecentServices, LowStock } from "@/pages/garage/GarageKpis";

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="g39-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[13px] text-muted-foreground"><Icon className="h-4 w-4 text-gold" />{label}</div>
    <div className="mt-2 font-display text-2xl font-bold" data-testid={`${testId}-value`}>{value}</div>
  </div>
);

const PlatformView = ({ data }) => {
  const { setCompany } = useSession();
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi icon={Building2} label="Companies" value={data.totals.companies} testId="kpi-companies" />
        <Kpi icon={GitBranch} label="Branches" value={data.totals.branches} testId="kpi-branches" />
        <Kpi icon={Users} label="Active users" value={data.totals.active_users} testId="kpi-active-users" />
      </div>
      <div className="g39-card mt-6 overflow-hidden" data-testid="platform-companies">
        <div className="px-4 py-3 border-b font-semibold text-sm">Companies</div>
        <div className="divide-y">
          {data.companies.map((c) => (
            <div key={c.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
              <div className="flex-1 min-w-[160px] font-medium">{c.name} <span className="text-muted-foreground font-mono-g text-xs ml-1">{c.code}</span></div>
              {c.is_demo && <Badge tone="demo">Demo</Badge>}
              <Badge tone={c.status}>{c.status}</Badge>
              <Button size="sm" variant="outline" onClick={() => setCompany(c.id)} data-testid={`open-company-${c.code}`}>Open</Button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default function Dashboard() {
  const { can, me, company, branch } = useSession();
  const { data, loading, error, reload } = useFetch("/dashboard", null, can("dashboard.view"));
  if (!can("dashboard.view")) return <NoAccess perm="dashboard.view" />;
  const first = (me?.user?.name || "").split(" ")[0];
  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Welcome${first ? `, ${first}` : ""}`} subtitle={company ? `${company.name} · ${branch ? branch.name : "All branches"}` : "All companies"} />
      {loading && !data && <div className="g39-card"><LoadingRows rows={4} /></div>}
      {error && <div className="g39-card"><ErrorState message={error} onRetry={reload} /></div>}
      {data?.scope === "platform" && <PlatformView data={data} />}
      {data?.scope === "company" && (
        <>
          <QuickActions />
          <GarageKpis />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {can("maintenance.view") && <RecentServices />}
            {can("inventory.view") && <LowStock />}
          </div>
        </>
      )}
    </div>
  );
}
